<script setup>
import DataTableLayout from '@js/components/tables/DataTableLayout.vue';
import BaseCell from '@js/components/tables/cells/BaseCell.vue';
import HeaderCell from '@js/components/tables/cells/HeaderCell.vue';
import PercentageCell from '@js/components/tables/cells/PercentageCell.vue';
import PriceCell from '@js/components/tables/cells/PriceCell.vue';
import TextCell from '@js/components/tables/cells/TextCell.vue';
import { useDataTable } from '@js/utils/dataTable';
import {
    formatPercentage,
    formatPrice,
    formatNumber,
} from '@js/utils/formatter';
import { useRoute } from '@js/utils/route';
import { createColumnHelper } from '@tanstack/vue-table';
import { h } from 'vue';

const { tenant, googleAdsKeywordManagement } = defineProps({
    tenant: {
        type: Object,
        required: true,
    },
    googleAdsKeywordManagement: {
        type: Object,
        required: true,
    },
});

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
    loading,
    table,
    count,
    globalFilter,
    filters,
    availableFilters,
    setGlobalFilter,
    setFilters,
} = useDataTable({
    columns: [
        columnHelper.accessor(() => null, {
            id: 'keyword',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Mot-clé',
                }),
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.keyword,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'adGroupName',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: "Groupe<br/> d'annonces",
                }),
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.adGroupName,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'type',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Type',
                }),
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.type,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'cpcMax',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'CPC<br/>Man. Act.',
                }),
            cell: (props) =>
                h(PriceCell, {
                    price: props.row.original.cpcMax,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'manualCpc',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'CPC<br/>Man. New.',
                }),
            cell: (props) =>
                h(PriceCell, {
                    price: props.row.original.manualCpc,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'cpcPercentage',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: '% CPC',
                }),
            cell: (props) =>
                h(PercentageCell, {
                    percentage: props.row.original.cpcPercentage,
                    withPlus: true,
                    type:
                        props.row.original.cpcPercentage === 0
                            ? undefined
                            : props.row.original.cpcPercentage >= 0
                              ? 'success'
                              : 'danger',
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'period',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Période',
                }),
            cell: () =>
                h(
                    BaseCell,
                    {
                        class: 'flex-col font-bold items-stretch whitespace-nowrap',
                    },
                    {
                        default: () => [
                            h('div', {}, [h('span', 'TOTAL')]),
                            h('div', {}, [h('span', '30J')]),
                        ],
                    },
                ),
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'averageCpc',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'CPC<br/>moyen',
                }),
            cell: (props) =>
                h(
                    BaseCell,
                    {
                        class: 'flex-col font-bold items-stretch whitespace-nowrap',
                    },
                    {
                        default: () => [
                            h('div', {}, [
                                h(
                                    'span',
                                    formatPrice(props.row.original.averageCpc),
                                ),
                            ]),
                            h('div', {}, [
                                formatPrice(
                                    props.row.original.averageCpc30Days,
                                ),
                            ]),
                        ],
                    },
                ),
        }),
        columnHelper.accessor(() => null, {
            id: 'searchRankLostImpressionShare',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Taux impr.<br/>perdues RR',
                }),
            cell: (props) =>
                h(
                    BaseCell,
                    {
                        class: 'flex-col font-bold items-stretch whitespace-nowrap',
                    },
                    {
                        default: () => [
                            h('div', {}, [
                                h(
                                    'span',
                                    formatPercentage(
                                        props.row.original
                                            .searchRankLostImpressionShare,
                                        false,
                                        true,
                                    ),
                                ),
                            ]),
                            h('div', {}, [
                                formatPercentage(
                                    props.row.original
                                        .searchRankLostImpressionShare30Days,
                                    false,
                                    true,
                                ),
                            ]),
                        ],
                    },
                ),
        }),
        columnHelper.accessor(() => null, {
            id: 'costPerConversion',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Coût/Conv.',
                }),
            cell: (props) =>
                h(
                    BaseCell,
                    {
                        class: 'flex-col font-bold items-stretch whitespace-nowrap',
                    },
                    {
                        default: () => [
                            h('div', {}, [
                                h(
                                    'span',
                                    formatPrice(
                                        props.row.original.costPerConversion,
                                    ),
                                ),
                            ]),
                            h('div', {}, [
                                formatPrice(
                                    props.row.original.costPerConversion30Days,
                                ),
                            ]),
                        ],
                    },
                ),
        }),
        columnHelper.accessor(() => null, {
            id: 'conversionValuePerCost',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Valeur<br/>conv./Coût',
                }),
            cell: (props) =>
                h(
                    BaseCell,
                    {
                        class: 'flex-col font-bold items-stretch whitespace-nowrap',
                    },
                    {
                        default: () => [
                            h('div', {}, [
                                h(
                                    'span',
                                    formatNumber(
                                        props.row.original
                                            .conversionValuePerCost,
                                    ),
                                ),
                            ]),
                            h('div', {}, [
                                formatNumber(
                                    props.row.original
                                        .conversionValuePerCost30Days,
                                ),
                            ]),
                        ],
                    },
                ),
        }),
        columnHelper.accessor(() => null, {
            id: 'clicks',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Clics',
                }),
            cell: (props) =>
                h(
                    BaseCell,
                    {
                        class: 'flex-col font-bold items-stretch whitespace-nowrap',
                    },
                    {
                        default: () => [
                            h('div', {}, [
                                h(
                                    'span',
                                    formatNumber(
                                        props.row.original.clicks,
                                        false,
                                    ),
                                ),
                            ]),
                            h('div', {}, [
                                formatNumber(
                                    props.row.original.clicks30Days,
                                    false,
                                ),
                            ]),
                        ],
                    },
                ),
        }),
        columnHelper.accessor(() => null, {
            id: 'costs',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Coûts',
                }),
            cell: (props) =>
                h(
                    BaseCell,
                    {
                        class: 'flex-col font-bold items-stretch whitespace-nowrap',
                    },
                    {
                        default: () => [
                            h('div', {}, [
                                h(
                                    'span',
                                    formatPrice(props.row.original.costs),
                                ),
                            ]),
                            h('div', {}, [
                                formatPrice(props.row.original.costs30Days),
                            ]),
                        ],
                    },
                ),
        }),
        columnHelper.accessor(() => null, {
            id: 'conversions',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Conv.',
                }),
            cell: (props) =>
                h(
                    BaseCell,
                    {
                        class: 'flex-col font-bold items-stretch whitespace-nowrap',
                    },
                    {
                        default: () => [
                            h('div', {}, [
                                h(
                                    'span',
                                    formatNumber(
                                        props.row.original.conversions,
                                    ),
                                ),
                            ]),
                            h('div', {}, [
                                formatNumber(
                                    props.row.original.conversions30Days,
                                ),
                            ]),
                        ],
                    },
                ),
        }),
        columnHelper.accessor(() => null, {
            id: 'conversionsValue',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Valeur<br/>conv.',
                }),
            cell: (props) =>
                h(
                    BaseCell,
                    {
                        class: 'flex-col font-bold items-stretch whitespace-nowrap',
                    },
                    {
                        default: () => [
                            h('div', {}, [
                                h(
                                    'span',
                                    formatNumber(
                                        props.row.original.conversionsValue,
                                    ),
                                ),
                            ]),
                            h('div', {}, [
                                formatNumber(
                                    props.row.original.conversionsValue30Days,
                                ),
                            ]),
                        ],
                    },
                ),
        }),
    ],
    url: route('front.projectManagement.sea.postKeywordManagementItems', {
        tenant: tenant.id,
        googleAdsKeywordManagement: googleAdsKeywordManagement.id,
    }),
    columnVisibility: {
        conversionsValuePerCost: tenant.type.value === 'e-commerce',
        conversionsValue: tenant.type.value === 'e-commerce',
    },
});
</script>

<template>
    <DataTableLayout
        :table="table"
        :count="count"
        :global-filter="globalFilter"
        :filters="filters"
        :available-filters="availableFilters"
        :set-filters="setFilters"
        :set-global-filter="setGlobalFilter"
        :loading="loading"
    />
</template>
